import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, ArrowLeft, ThumbsUp, ThumbsDown, Sparkles, Loader2, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { listInferences, voteInference, generateInferences } from "../lib/api";

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 w-16 bg-zinc-900">
        <div className="h-full bg-fuchsia-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-[10px] text-fuchsia-300">{pct}%</span>
    </div>
  );
}

export default function InferenceQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [votingId, setVotingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listInferences("pending", 50)
      .then((r) => setRows(r?.inferences || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const doGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await generateInferences();
      if ((res?.generated || 0) === 0) {
        toast.info("No new derivations found — the library needs more triples.");
      } else {
        toast.success(`Generated ${res.generated} new derivation${res.generated === 1 ? "" : "s"}.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const doVote = async (inferenceId, vote) => {
    if (votingId) return;
    setVotingId(inferenceId);
    try {
      const res = await voteInference(inferenceId, vote);
      if (res.promoted) {
        toast.success("Promoted to the neuro-symbolic library! ✨");
      } else {
        toast.success(`Voted ${vote} · ${res.votes_up}👍 / ${res.votes_down}👎`);
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vote failed.");
    } finally {
      setVotingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="infer-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <div className="border-b border-zinc-900 pb-5 mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-fuchsia-300 mb-2">
            <Brain className="h-3 w-3" /> Symbolic inference queue
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.05em]">
            Reason from the graph.
          </h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-3xl leading-relaxed">
            The compositional layer. Given two anchored triples like{" "}
            <span className="text-emerald-300 font-mono">A → founded_by → B</span> and{" "}
            <span className="text-emerald-300 font-mono">B → born_in → C</span>, Gemini
            proposes derived facts like{" "}
            <span className="text-fuchsia-300 font-mono">A → indirectly_traceable_to → C</span>.
            Community votes. When a proposal reaches <span className="text-emerald-300">3 up-votes</span>{" "}
            and clears a 2:1 ratio, it auto-promotes to the library as a verified fact.
            This is how the knowledge graph reasons about itself.
          </p>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {rows.length} pending derivation{rows.length === 1 ? "" : "s"}
          </div>
          <button
            onClick={doGenerate}
            disabled={generating}
            data-testid="infer-generate-btn"
            className="inline-flex items-center gap-2 px-3 py-2 border border-fuchsia-500 text-fuchsia-300 hover:bg-fuchsia-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.25em]"
          >
            {generating ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reasoning…</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5" /> Generate new derivations</>
            )}
          </button>
        </div>

        {loading && rows.length === 0 ? (
          <div className="text-zinc-500 text-[11px] uppercase tracking-widest text-center py-8">
            Loading queue…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="text-zinc-600 text-[11px] uppercase tracking-widest text-center py-12 border border-dashed border-zinc-800"
            data-testid="infer-empty"
          >
            No pending derivations · click "Generate" to have Gemini reason from the graph
          </div>
        ) : (
          <ul className="space-y-2" data-testid="infer-list">
            {rows.map((r) => (
              <li
                key={r.id}
                data-testid={`infer-${r.id}`}
                className="border border-zinc-800 bg-[#0b0b0d] p-4"
              >
                <div className="flex flex-wrap items-center gap-2 mb-2 text-sm font-mono">
                  <span className="text-zinc-100">{r.subject}</span>
                  <span className="text-fuchsia-300 text-[10px] uppercase tracking-widest">
                    {r.predicate}
                  </span>
                  <span className="text-zinc-100">{r.object}</span>
                  <span className="ml-auto"><ConfidenceBar value={r.confidence} /></span>
                </div>
                {r.reasoning && (
                  <div className="text-[11px] text-zinc-500 italic mb-3">
                    {r.reasoning}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => doVote(r.id, "up")}
                    disabled={!!votingId}
                    data-testid={`infer-up-${r.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 transition-colors text-[10px] uppercase tracking-widest"
                  >
                    <ThumbsUp className="h-3 w-3" /> {r.votes_up}
                  </button>
                  <button
                    onClick={() => doVote(r.id, "down")}
                    disabled={!!votingId}
                    data-testid={`infer-down-${r.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-rose-500/60 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 transition-colors text-[10px] uppercase tracking-widest"
                  >
                    <ThumbsDown className="h-3 w-3" /> {r.votes_down}
                  </button>
                  <div className="ml-auto text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
                    3 up-votes → auto-promote
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="text-center mt-8 flex gap-4 justify-center flex-wrap">
          <Link to="/knowledge-graph" data-testid="infer-to-graph" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300">
            <Zap className="h-3 w-3" /> Browse the truth graph
          </Link>
          <Link to="/obsidian" data-testid="infer-to-obsidian" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-fuchsia-300">
            <Brain className="h-3 w-3" /> Run a deep-dive
          </Link>
        </div>
      </div>
    </div>
  );
}
